import type { AnchorHTMLAttributes, ButtonHTMLAttributes, ReactNode } from 'react'
import { cn } from '../lib/cn'

type Variant = 'primary' | 'secondary'

type CommonProps = {
  variant?: Variant
  className?: string
  children: ReactNode
}

type ButtonAsLink = CommonProps &
  Omit<AnchorHTMLAttributes<HTMLAnchorElement>, 'className' | 'children'> & {
    href: string
  }

type ButtonAsButton = CommonProps &
  Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'className' | 'children'> & {
    href?: undefined
  }

export type ButtonProps = ButtonAsLink | ButtonAsButton

const classes = (variant: Variant, className?: string) =>
  cn(
    'vcn-btn',
    variant === 'primary' ? 'vcn-btn--primary' : 'vcn-btn--secondary',
    className,
  )

/**
 * Pill-Button in den beiden Ausprägungen aus dem Design:
 * hell mit Verlauf (primary) und dunkel/transluzent (secondary).
 *
 * Mit `href` wird ein `<a>` gerendert, sonst ein `<button>`.
 */
export function Button(props: ButtonProps) {
  if (props.href !== undefined) {
    const { variant = 'primary', className, children, ...rest } = props
    const external = /^https?:\/\//.test(props.href)
    return (
      <a
        className={classes(variant, className)}
        {...(external ? { rel: 'noopener noreferrer' } : {})}
        {...rest}
      >
        {children}
      </a>
    )
  }

  const { variant = 'primary', className, children, type, ...rest } = props
  return (
    <button type={type ?? 'button'} className={classes(variant, className)} {...rest}>
      {children}
    </button>
  )
}
